import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Search, X, MessageCircle, ArrowRight } from "lucide-react";
import { CITIES, COUNTRIES } from "@/cityConfig";

const WA = "905316224378";

const RU2LAT = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "i", к: "k", л: "l", м: "m",
  н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sh", ъ: "",
  ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

const norm = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zа-яё0-9]+/g, " ")
    .trim();

const translit = (s = "") => norm(s).replace(/[а-яё]/g, (ch) => RU2LAT[ch] ?? ch).replace(/\s+/g, " ");

const ALL = Object.values(CITIES).map((c) => {
  const country = COUNTRIES[c.country] || {};
  const keys = [c.name, c.nameEn, c.nameTr, c.slug.replace(/-/g, " "), country.name, country.nameEn, country.nameTr]
    .filter(Boolean)
    .flatMap((k) => [norm(k), translit(k)]);
  return { city: c, country, keys: [...new Set(keys)] };
});

const TXT = {
  ru: {
    placeholder: "Введите город доставки",
    hint: "Начните печатать — подскажем варианты",
    notFound: (q) => `Города «${q}» пока нет в списке`,
    notFoundSub: "Напишите менеджеру в WhatsApp — уточним, сможем ли доставить букет туда.",
    wa: "Уточнить в WhatsApp",
    waMsg: (q) => `Здравствуйте! Хочу заказать доставку цветов в город ${q}. Сможете ли вы туда доставить?`,
  },
  en: {
    placeholder: "Type your delivery city",
    hint: "Start typing — we’ll suggest matches",
    notFound: (q) => `“${q}” is not on our list yet`,
    notFoundSub: "Message our manager on WhatsApp — we’ll check whether we can deliver there.",
    wa: "Ask on WhatsApp",
    waMsg: (q) => `Hello! I’d like to order flower delivery to ${q}. Can you deliver there?`,
  },
  tr: {
    placeholder: "Teslimat şehrini yazın",
    hint: "Yazmaya başlayın — seçenekleri önerelim",
    notFound: (q) => `“${q}” henüz listemizde yok`,
    notFoundSub: "WhatsApp’tan yöneticimize yazın — oraya teslimat yapabilir miyiz kontrol edelim.",
    wa: "WhatsApp’tan sor",
    waMsg: (q) => `Merhaba! ${q} şehrine çiçek siparişi vermek istiyorum. Oraya teslimat yapabilir misiniz?`,
  },
};

const cityLabel = (c, lang) => (lang === "tr" ? c.nameTr || c.nameEn : lang === "en" ? c.nameEn : c.name);
const countryLabel = (c, lang) => (lang === "tr" ? c.nameTr || c.nameEn : lang === "en" ? c.nameEn : c.name);

const Highlight = ({ text, query }) => {
  if (!query) return text;
  const i = norm(text).indexOf(norm(query));
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-transparent text-pink-600 font-semibold">{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </>
  );
};

export const useCitySearch = (query) => {
  const results = useMemo(() => {
    const q = norm(query);
    const qt = translit(query);
    const list = q ? ALL.filter(({ keys }) => keys.some((k) => k.includes(q) || k.includes(qt))) : ALL;
    const groups = [];
    Object.keys(COUNTRIES).forEach((slug) => {
      const items = list.filter((r) => r.city.country === slug);
      if (items.length) groups.push({ slug, country: COUNTRIES[slug], items });
    });
    return groups;
  }, [query]);
  const flat = useMemo(() => results.flatMap((g) => g.items.map((r) => r.city)), [results]);
  return { results, flat };
};

/* Search input + grouped results + WhatsApp fallback. No positioning — embed anywhere. */
export function CitySearchPanel({
  lang = "ru",
  selectedSlug,
  onSelect,
  autoFocus = false,
  compact = false,
  listMaxClass = "max-h-[min(60vh,420px)]",
  testIdPrefix = "dropdown-city",
}) {
  const t = TXT[lang] || TXT.ru;
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const { results, flat } = useCitySearch(query);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (active < 0 || !listRef.current) return;
    listRef.current.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = flat[active] ?? (flat.length === 1 ? flat[0] : null);
      if (c) onSelect(c);
    }
  };

  const showEmpty = query.trim().length > 0 && flat.length === 0;
  const waHref = `https://wa.me/${WA}?text=${encodeURIComponent(t.waMsg(query.trim()))}`;
  let idx = -1;

  return (
    <div className="text-left" data-testid="city-search-panel">
      <div className={`flex items-center gap-2 border-b border-gray-100 ${compact ? "px-3 py-2" : "px-4 py-3"}`}>
        <Search className="w-4 h-4 text-gray-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          role="combobox"
          aria-expanded="true"
          aria-autocomplete="list"
          value={query}
          placeholder={t.placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          className="flex-1 min-w-0 bg-transparent outline-none text-[16px] sm:text-sm text-gray-900 placeholder:text-gray-400 font-medium"
          data-testid="header-city-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setActive(-1);
              inputRef.current?.focus();
            }}
            aria-label="Clear"
            className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            data-testid="header-city-search-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showEmpty ? (
        <div className="p-4" data-testid="header-city-search-empty">
          <p className="text-sm font-semibold text-gray-900">{t.notFound(query.trim())}</p>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t.notFoundSub}</p>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-2 bg-[#25d366] hover:bg-[#20bd5a] text-white px-4 py-2 rounded-full text-xs font-bold transition-colors"
            data-testid="header-city-search-whatsapp"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            {t.wa}
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      ) : (
        <ul
          role="listbox"
          ref={listRef}
          className={`${listMaxClass} overflow-y-auto overscroll-contain py-1`}
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {!query && <li className="px-4 pt-2 pb-1 text-[11px] text-gray-400">{t.hint}</li>}
          {results.map((g) => (
            <li key={g.slug}>
              <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 sticky top-0 bg-white/95 backdrop-blur-sm">
                <span>{g.country.flag}</span>
                {countryLabel(g.country, lang)}
              </div>
              <ul>
                {g.items.map(({ city }) => {
                  idx += 1;
                  const i = idx;
                  const isSel = city.slug === selectedSlug;
                  const isAct = i === active;
                  return (
                    <li key={city.slug} role="option" aria-selected={isSel} data-idx={i}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => onSelect(city)}
                        className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${isAct || isSel ? "bg-pink-50" : ""}`}
                        data-testid={`${testIdPrefix}-${city.slug}`}
                      >
                        <MapPin className={`w-4 h-4 shrink-0 ${isSel || isAct ? "text-pink-500" : "text-gray-300"}`} />
                        <span className={`text-sm ${isSel ? "text-pink-600 font-semibold" : "text-gray-700"}`}>
                          <Highlight text={cityLabel(city, lang)} query={query} />
                        </span>
                        {query && lang !== "en" && norm(city.nameEn) !== norm(cityLabel(city, lang)) && (
                          <span className="ml-auto text-xs text-gray-400">{city.nameEn}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* Homepage hero variant: white pill input, dropdown below, fills "City, Country" on select. */
export default function CitySearchCombobox({ lang = "ru", selectedSlug, onSelect }) {
  const t = TXT[lang] || TXT.ru;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const { results, flat } = useCitySearch(query);

  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, []);

  useEffect(() => {
    if (active < 0 || !listRef.current) return;
    listRef.current.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const choose = (city) => {
    onSelect(city.slug);
    setQuery(`${cityLabel(city, lang)}, ${countryLabel(COUNTRIES[city.country] || {}, lang)}`);
    setOpen(false);
    setActive(-1);
    inputRef.current?.blur();
  };

  const clear = () => {
    setQuery("");
    onSelect("");
    setActive(-1);
    setOpen(true);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = flat[active] ?? (flat.length === 1 ? flat[0] : null);
      if (c) choose(c);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const selected = selectedSlug ? CITIES[selectedSlug] : null;
  const showEmpty = open && query.trim().length > 0 && flat.length === 0;
  const waHref = `https://wa.me/${WA}?text=${encodeURIComponent(t.waMsg(query.trim()))}`;
  let idx = -1;

  return (
    <div className="max-w-md mx-auto mb-6 relative text-left" ref={rootRef} data-testid="city-search">
      <div
        className={`flex items-center gap-3 bg-white rounded-2xl pl-5 pr-3 py-3.5 shadow-xl shadow-black/20 border transition-colors ${
          open ? "border-pink-400 ring-4 ring-pink-500/15" : "border-white/80 hover:border-pink-300"
        }`}
      >
        <MapPin className={`w-5 h-5 shrink-0 ${selected ? "text-pink-500" : "text-gray-400"}`} />
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={open}
          aria-controls="city-search-list"
          aria-autocomplete="list"
          value={query}
          placeholder={t.placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(-1);
            setOpen(true);
            if (selectedSlug) onSelect("");
          }}
          onKeyDown={onKeyDown}
          className="flex-1 min-w-0 bg-transparent outline-none text-[16px] text-gray-900 placeholder:text-gray-400 font-medium"
          data-testid="city-search-input"
        />
        {query ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear"
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            data-testid="city-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <Search className="w-4 h-4 text-gray-300 mr-1.5" />
        )}
      </div>

      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-fade-in"
          data-testid="city-search-dropdown"
        >
          {showEmpty ? (
            <div className="p-5" data-testid="city-search-empty">
              <p className="text-sm font-semibold text-gray-900">{t.notFound(query.trim())}</p>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">{t.notFoundSub}</p>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 bg-[#25d366] hover:bg-[#20bd5a] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors"
                data-testid="city-search-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                {t.wa}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <ul
              id="city-search-list"
              role="listbox"
              ref={listRef}
              className="max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain py-1"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {!query && <li className="px-4 pt-2 pb-1 text-[11px] text-gray-400">{t.hint}</li>}
              {results.map((g) => (
                <li key={g.slug}>
                  <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 sticky top-0 bg-white/95 backdrop-blur-sm">
                    <span>{g.country.flag}</span>
                    {countryLabel(g.country, lang)}
                  </div>
                  <ul>
                    {g.items.map(({ city }) => {
                      idx += 1;
                      const i = idx;
                      const isSel = city.slug === selectedSlug;
                      const isAct = i === active;
                      return (
                        <li key={city.slug} role="option" aria-selected={isSel} data-idx={i}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setActive(i)}
                            onClick={() => choose(city)}
                            className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${isAct || isSel ? "bg-pink-50" : ""}`}
                            data-testid={`dropdown-city-${city.slug}`}
                          >
                            <MapPin className={`w-4 h-4 shrink-0 ${isSel || isAct ? "text-pink-500" : "text-gray-300"}`} />
                            <span className={`text-sm ${isSel ? "text-pink-600 font-semibold" : "text-gray-700"}`}>
                              <Highlight text={cityLabel(city, lang)} query={query} />
                            </span>
                            {query && lang !== "en" && norm(city.nameEn) !== norm(city.name) && (
                              <span className="ml-auto text-xs text-gray-400">{city.nameEn}</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
